import type { ReactNode } from 'react'
import { cn } from '@/utils/cn'
import { getMessage } from '@/utils/messages'

interface ConfirmDialogProps {
  open: boolean
  title: string
  message: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  hideCancel?: boolean
  loading?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Lightweight modal confirmation dialog.
 * No external dependency — uses a backdrop div with pointer-events.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = getMessage('common.button.confirm'),
  cancelLabel = getMessage('common.button.cancel'),
  hideCancel = false,
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  if (!open) return null

  return (
    <>
      <div
        className="fixed inset-0 z-[400] bg-black/40 backdrop-blur-[2px]"
        onClick={loading ? undefined : onCancel}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        className={cn(
          'fixed z-[401] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
          'w-full max-w-sm bg-[var(--surface)] rounded-[var(--radius)]',
          'border border-[var(--border)] shadow-2xl animate-fade-in',
          'p-6'
        )}
      >
        <h2
          id="confirm-title"
          className="text-[15px] font-bold text-[var(--text)] mb-2"
          style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
        >
          {title}
        </h2>
        <p className="text-[12px] text-[var(--text-dim)] font-mono leading-relaxed mb-6">
          {message}
        </p>

        <div className="flex gap-3 justify-end">
          {!hideCancel && (
            <button
              onClick={onCancel}
              disabled={loading}
              className={cn(
                'px-4 py-2 rounded-[var(--radius-sm)] border border-[var(--border)]',
                'bg-[var(--surface2)] text-[var(--text-dim)] font-mono text-[12px]',
                'hover:border-[var(--accent)] hover:text-[var(--accent)] transition-all duration-150',
                loading && 'opacity-40 cursor-not-allowed pointer-events-none'
              )}
            >
              {cancelLabel}
            </button>
          )}
          <button
            onClick={onConfirm}
            disabled={loading}
            className={cn(
              'flex items-center gap-2 px-5 py-[9px] rounded-[var(--radius-sm)]',
              'text-white text-[12px] font-medium',
              'transition-all duration-[180ms] border border-transparent',
              loading ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-90 cursor-pointer'
            )}
            style={{
              background: 'linear-gradient(135deg, var(--accent), #2563eb)',
            }}
          >
            {loading && (
              <span className="w-3 h-3 rounded-full border-2 border-white/40 border-t-white animate-spin flex-shrink-0" />
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </>
  )
}
